/**
 * Admin/Devices.tsx — device management: rename / unenroll (SPEC §3
 * PATCH/DELETE /devices/{id}; org_admin) + per-org branded installer generation
 * (SPEC §7 POST /installers).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import type { Device, InstallerResult } from '@/types';

type InstallerOS = 'windows' | 'linux' | 'darwin';

const OS_OPTIONS: { value: InstallerOS; label: string }[] = [
  { value: 'windows', label: 'Windows' },
  { value: 'linux', label: 'Linux' },
  { value: 'darwin', label: 'macOS' },
];

export default function ManageDevices() {
  const api = useApi();
  const qc = useQueryClient();
  const { data: devices = [], isLoading } = useQuery({ queryKey: ['devices'], queryFn: () => api.listDevices() });
  // The build the deployment publishes as "latest"; drives the version badges +
  // the "Update from RemoteHelm" actions.
  const { data: release } = useQuery({ queryKey: ['agent-release'], queryFn: () => api.getAgentRelease() });

  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [updateMsg, setUpdateMsg] = useState<string | null>(null);

  // outdated = the device reports a version AND it differs from the published target.
  const isOutdated = (d: Device) =>
    !!release?.version && !!d.agent_version && d.agent_version !== release.version;
  const outdatedOnline = devices.filter((d: Device) => isOutdated(d) && d.online).length;

  const updateOne = useMutation({
    mutationFn: (id: string) => api.updateAgent(id),
    onSuccess: (r) => {
      setUpdateMsg(
        r.status === 'already_current'
          ? 'Agent is already on the latest build.'
          : `Update to ${r.version} sent — the agent will download, verify, and restart.`,
      );
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: () => setUpdateMsg('Could not send the update (device offline or no published build).'),
  });

  const updateAll = useMutation({
    mutationFn: () => api.updateAgentsBulk(),
    onSuccess: (r) => {
      setUpdateMsg(
        `Update to ${r.version} sent to ${r.pushed} device${r.pushed === 1 ? '' : 's'}` +
          (r.offline_skipped ? ` (${r.offline_skipped} offline, skipped).` : '.'),
      );
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: () => setUpdateMsg('Could not push fleet update.'),
  });

  // Build-installer form state.
  const [os, setOs] = useState<InstallerOS>('windows');
  const [format, setFormat] = useState<'exe' | 'msi'>('exe');
  const [buildName, setBuildName] = useState('');
  const [buildGroup, setBuildGroup] = useState('');
  const [installer, setInstaller] = useState<InstallerResult | null>(null);
  const [downloadUrl, setDownloadUrl] = useState('');

  const rename = useMutation({
    mutationFn: (v: { id: string; name: string }) => api.updateDevice(v.id, { name: v.name }),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const unenroll = useMutation({
    mutationFn: (id: string) => api.deleteDevice(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['devices'] }),
  });

  const makeInstaller = useMutation({
    mutationFn: () => api.createInstaller({ label: 'console' }),
    onSuccess: (r) => {
      setInstaller(r);
      setDownloadUrl(
        api.installerDownloadUrl(r, {
          os,
          arch: 'amd64',
          name: buildName.trim() || undefined,
          group: buildGroup.trim() || undefined,
          format: os === 'windows' ? format : undefined,
        }),
      );
    },
  });

  const osLabel = OS_OPTIONS.find((o) => o.value === os)?.label ?? os;

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-helm-text">Manage Devices</h1>
          <p className="mt-0.5 text-sm text-helm-muted">
            Rename or unenroll endpoints, build access installers, and push agent updates.
          </p>
          {release?.version && (
            <p className="mt-1 text-xs text-helm-muted">
              Published agent build: <span className="font-mono text-helm-text">{release.version}</span>
              {release.auto_update ? ' · auto-update on' : ' · auto-update off'}
            </p>
          )}
        </div>
        {outdatedOnline > 0 && (
          <button
            type="button"
            onClick={() => {
              setUpdateMsg(null);
              updateAll.mutate();
            }}
            disabled={updateAll.isPending}
            className="rh-btn-primary"
          >
            {updateAll.isPending ? 'Updating…' : `Update all out-of-date (${outdatedOnline})`}
          </button>
        )}
      </div>
      {updateMsg && (
        <div className="mb-4 rounded-md border border-helm-border bg-helm-bg/60 px-3 py-2 text-sm text-helm-text">
          {updateMsg}
        </div>
      )}

      {/* ---- Build access installer ---- */}
      <div className="rh-card p-5">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-helm-accent/15 text-helm-accent">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
            </svg>
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-helm-text">Build access installer</h2>
            <p className="mt-0.5 text-sm text-helm-muted">
              One self-contained file with this org&rsquo;s settings baked in. Run it on a target machine to enroll it
              under Access — unattended, no console steps.
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-helm-muted/70">
              Operating system
            </span>
            <select
              value={os}
              onChange={(e) => setOs(e.target.value as InstallerOS)}
              className="rh-input"
            >
              {OS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {os === 'windows' && (
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-helm-muted/70">
                Package
              </span>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value as 'exe' | 'msi')}
                className="rh-input"
              >
                <option value="exe">Signed .exe (one file)</option>
                <option value="msi">Signed .msi (SCCM / Intune)</option>
              </select>
            </label>
          )}
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-helm-muted/70">
              Device name <span className="normal-case text-helm-muted/60">(optional)</span>
            </span>
            <input
              value={buildName}
              onChange={(e) => setBuildName(e.target.value)}
              placeholder="e.g. Front-desk PC"
              className="rh-input"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-helm-muted/70">
              Group <span className="normal-case text-helm-muted/60">(optional)</span>
            </span>
            <input
              value={buildGroup}
              onChange={(e) => setBuildGroup(e.target.value)}
              placeholder="e.g. Servers"
              className="rh-input"
            />
          </label>
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={() => makeInstaller.mutate()}
            disabled={makeInstaller.isPending}
            className="rh-btn-primary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l6-6a4 4 0 0 0 5.4-5.4l-2.6 2.6-2-2 2.6-2.6Z" />
            </svg>
            {makeInstaller.isPending ? 'Building…' : 'Build installer'}
          </button>
          {makeInstaller.isError && (
            <span className="ml-3 text-sm text-helm-bad">Build failed. Please try again.</span>
          )}
        </div>

        {installer && downloadUrl && (
          <div className="mt-4 rounded-lg border border-helm-border bg-helm-bg/60 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-helm-text">Installer ready</div>
                <div className="mt-0.5 text-xs text-helm-muted">
                  {osLabel}
                  {os === 'windows' ? (format === 'msi' ? ' · signed .msi' : ' · signed .exe') : ''} · org{' '}
                  <span className="font-mono text-helm-text">{installer.config.org_id}</span>
                </div>
              </div>
              <a
                href={downloadUrl}
                download
                className="rh-btn-primary"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download {os === 'windows' ? (format === 'msi' ? '.msi' : '.exe') : 'installer'}
              </a>
            </div>
            <p className="mt-3 text-xs text-helm-warn">
              {os === 'windows' && format === 'msi'
                ? 'Signed MSI — deploy silently via SCCM/Intune (msiexec /i … /qn) or run it directly. '
                : 'Single file — run it on the target machine to enroll it under Access. '}
              The embedded token is reusable for this org, so treat the file as a secret.
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 rh-card overflow-hidden">
        <table className="rh-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Hostname</th>
              <th>OS</th>
              <th>Version</th>
              <th>Type</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d: Device) => (
              <tr key={d.id}>
                <td className="font-medium text-helm-text">
                  {editing === d.id ? (
                    <input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="rh-input w-48"
                    />
                  ) : (
                    d.name || '—'
                  )}
                </td>
                <td className="text-helm-muted">{d.hostname || '—'}</td>
                <td className="text-helm-muted">
                  {d.os} {d.os_version}
                </td>
                <td className="text-helm-muted">
                  {d.agent_version ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-mono text-xs">{d.agent_version}</span>
                      {isOutdated(d) ? (
                        <span className="rh-badge bg-helm-warn/15 text-helm-warn">update available</span>
                      ) : (
                        release?.version && (
                          <span className="rh-badge bg-helm-good/15 text-helm-good">up to date</span>
                        )
                      )}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {d.ephemeral ? (
                    <span className="rh-badge bg-helm-warn/15 text-helm-warn">attended</span>
                  ) : (
                    <span className="rh-badge bg-helm-elevated text-helm-muted">unattended</span>
                  )}
                </td>
                <td className="text-right">
                  {editing === d.id ? (
                    <div className="inline-flex gap-2">
                      <button
                        type="button"
                        onClick={() => rename.mutate({ id: d.id, name: editName })}
                        className="rh-btn-primary px-2.5 py-1 text-xs"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditing(null)}
                        className="rh-btn-ghost px-2.5 py-1 text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="inline-flex gap-2">
                      {isOutdated(d) && d.online && (
                        <button
                          type="button"
                          onClick={() => {
                            setUpdateMsg(null);
                            updateOne.mutate(d.id);
                          }}
                          disabled={updateOne.isPending}
                          className="rh-btn-ghost border-helm-accent/40 px-2.5 py-1 text-xs text-helm-accent hover:bg-helm-accent/10"
                          title={`Update to ${release?.version}`}
                        >
                          Update
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(d.id);
                          setEditName(d.name);
                        }}
                        className="rh-btn-ghost px-2.5 py-1 text-xs"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => unenroll.mutate(d.id)}
                        className="rh-btn-ghost border-helm-bad/40 px-2.5 py-1 text-xs text-helm-bad hover:bg-helm-bad/10"
                      >
                        Unenroll
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {isLoading && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-helm-muted">
                  Loading devices…
                </td>
              </tr>
            )}
            {devices.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="py-12 text-center text-helm-muted">
                  No devices found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
